import { IBreadcrumbItem, IComboBoxOption, PivotItem } from '@fluentui/react';
import {
  debounce,
  Dropdown,
  getDescriptionForItemCount,
  IconButton,
  iconButtonStylesToggle,
  ISearchProps,
  PageHeader,
  pageHeaderStylesSecondary,
  Pivot,
  pivotContainerStylesFill,
  pivotStylesFill,
  Search,
  useClassNames,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useMemo, useState } from 'react';

import { JobList } from '../../../components/JobList/JobList';
import { JobStatus, JobType } from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle, jobTypeNameMap } from '../../../featurestore/models';
import { featurestorePageClassName } from '../../../featurestore/styles';
import { getDropdownOptionsByKeyNameMap } from '../../../featurestore/utils';
import { useSearchParams } from '../../../utils/hooks';
import { IJobsClassNames, IJobsStyles, jobsStylesDefault } from './Jobs.styles';

const getBreadcrumbItems = (currentTitle = ContentsTitle.allJobs) => {
  return [
    { text: ContentsTitle.jobs, key: ContentsTitle.jobs },
    {
      text: currentTitle,
      key: currentTitle,
      isCurrentItem: true,
    },
  ];
};

export const Jobs = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('jobstab');
  const jobType = params.get('jobtype');
  const [isCardView, setIsCardView] = useState(true);
  const [description, setDescription] = useState('');
  const [selectedPivotKey, setSelectedPivotKey] = useState(tab ? tab : ContentsTitle.allJobs);
  const [searchText, setSearchText] = useState('');
  const [jobTypeFilters, setJobTypeFilters] = useState<JobType[]>(jobType ? [jobType as JobType] : []);
  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumbItem[]>(getBreadcrumbItems());
  const changeSearchText = useCallback((text: string) => {
    setSearchText(text);
  }, []);
  const onSearchTextChange = useMemo(() => debounce(300, changeSearchText), [changeSearchText]);
  const onUpdateData = useCallback((count: number, pageSize: number, pageToken: string, name: string) => {
    setDescription(getDescriptionForItemCount(count, pageSize, pageToken, name));
  }, []);
  const onChangeJobTypeFilters = (_e: FormEvent<HTMLDivElement>, option?: IComboBoxOption) => {
    if (option) {
      const key = option.key as JobType;
      if (option.selected) {
        if (!jobTypeFilters.includes(key)) {
          setJobTypeFilters([...jobTypeFilters, key]);
        }
      } else {
        if (jobTypeFilters.includes(key)) {
          setJobTypeFilters(jobTypeFilters.filter((s) => s !== key));
        }
      }
    }
  };
  const searchProps: ISearchProps = {
    placeholder: 'Search Jobs by Feature set name',
    onSearchTextChange,
  };
  const classNames = useClassNames<IJobsStyles, IJobsClassNames>(
    'Jobs',
    jobsStylesDefault,
    undefined,
    featurestorePageClassName
  );
  const renderPivotContent = (jobStatus?: JobStatus) => (
    <div className={classNames.list} data-test="pivot-job-list">
      <div className={classNames.description}>
        <div className={classNames.descriptionText}>{description}</div>
      </div>
      <div className={classNames.search}>
        <Search style={{ maxWidth: '611px', flex: 1 }} {...searchProps} />
        <div className={classNames.toggleButtons}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Dropdown
              style={{ width: '300px' }}
              placeholder="Filter by job types"
              data-test="dropdown-job-type-filter"
              multiSelect
              selectedKeys={jobTypeFilters}
              options={getDropdownOptionsByKeyNameMap(jobTypeNameMap)}
              onChange={onChangeJobTypeFilters}
            />
            <div style={{ paddingTop: '6px' }}>
              <IconButton
                style={{ marginRight: 8 }}
                styles={iconButtonStylesToggle}
                iconName="ExpandMenu"
                checked={isCardView}
                onClick={() => {
                  setIsCardView(true);
                }}
              />
              <IconButton
                styles={iconButtonStylesToggle}
                iconName="CollapseMenu"
                checked={!isCardView}
                onClick={() => {
                  setIsCardView(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <JobList
        isCardView={isCardView}
        jobStatus={jobStatus}
        jobTypeFilters={jobTypeFilters}
        featureSetName={searchText}
        onUpdateData={onUpdateData}
      />
    </div>
  );

  return (
    <div className={classNames.root} data-test="jobs">
      <PageHeader
        styles={pageHeaderStylesSecondary}
        style={{ marginBottom: 16 }}
        pageTitle={ContentsTitle.allJobs}
        breadcrumbProps={{
          items: breadcrumbItems,
        }}
      />
      <Pivot
        styles={pivotStylesFill}
        pivotContainerStyles={pivotContainerStylesFill}
        selectedKey={selectedPivotKey}
        onLinkClick={(item?: PivotItem) => {
          const title = item?.props.itemKey as ContentsTitle;
          if (title) {
            setBreadcrumbItems(getBreadcrumbItems(title));
            setSelectedPivotKey(title);
            params.set('jobstab', title);
            setParams(params);
          }
        }}
        items={[
          {
            key: ContentsTitle.allJobs,
            headerText: ContentsTitle.allJobs,
            content: renderPivotContent(),
          },
          {
            key: ContentsTitle.runningJobs,
            headerText: ContentsTitle.runningJobs,
            content: renderPivotContent(JobStatus.Running),
          },
          {
            key: ContentsTitle.failedJobs,
            headerText: ContentsTitle.failedJobs,
            content: renderPivotContent(JobStatus.Failed),
          },
          {
            key: ContentsTitle.pendingJobs,
            headerText: ContentsTitle.pendingJobs,
            content: renderPivotContent(JobStatus.Pending),
          },
          {
            key: ContentsTitle.successfulJobs,
            headerText: ContentsTitle.successfulJobs,
            content: renderPivotContent(JobStatus.Success),
          },
        ]}
      />
    </div>
  );
};
